import type {ScanContext} from "../scan-model/ScanContext.tsx";
import type {
    ApiExtractProfileEntry,
    ApiQueryProfileEntry,
    CloudCredentialEntry,
    OAuthEntry,
    PasswordInfo,
    ProjectErrorReporting,
    ProjectScmEntry, QueueInfo,
    ScanResult
} from "../scan-model/ScanResult.tsx";
import type {ProjectId} from "../../../metl-models/id/ProjectId.tsx";
import {runPromises} from "../../../util/promises/PromiseRunner.tsx";
import type {VersionsScanPlan} from "./2-ScanVersions.tsx";
import {isQueueEnabled} from "../../../metl-models/exports/MetlQueueExport.ts";

export async function scanSCM(ctx: ScanContext, projects: ProjectId[]): Promise<ProjectScmEntry[] | undefined> {
    const {log, logError, options, api} = ctx
    if(!options.analyseGit) return undefined
    log("Analysing Git configurations")
    return runPromises<ProjectId, ProjectScmEntry>(undefined, projects, async (projectId) => {
        try {
            const gitApi = api.project(projectId).git()
            const gitState = await gitApi.getState()
            if(!gitState) return {projectId, hasScm: false}
            const remote = await gitApi.getRemoteUrl()
            let remoteHost: string | undefined = undefined
            if(remote) {
                try {
                    const url = new URL(remote)
                    remoteHost = url.hostname
                } catch {
                    remoteHost = "unknown"
                }
            }
            return {
                projectId,
                hasScm: true,
                scmRemoteHost: remoteHost
            }
        } catch (e) {
            logError(`An error occurred while scanning git configuration [${projectId.groupName}/${projectId.projectName}]`, e)
            return {
                projectId,
                hasScm: undefined,
                scmRemoteHost: undefined
            }
        }
    })
}

export type ApiQueryScanResult = Pick<ScanResult, "apiExtract" | "apiQuery">
export async function analyseApiProfiles(ctx: ScanContext): Promise<ApiQueryScanResult> {
    const {api, options, log, logError} = ctx
    if(!options.analyseApiProfiles) return {}
    try {
        log("Scanning API Query Profiles")
        const profiles = await api.apiQuery.list()
        const profileResult = await runPromises<string, ApiQueryProfileEntry>(undefined, profiles, async (profile) => {
            try {
                const files = await api.apiQuery.get(profile).files.list()
                const rsbCount = files.filter(x => x.endsWith(".rsb")).length
                return {
                    name: profile,
                    fileCount: files.length,
                    rsbFileCount: rsbCount
                }
            } catch(e) {
                logError(`An error occurred while scanning API Query profile [${profile}]`, e)
                return {
                    name: profile,
                    fileCount: -1,
                    rsbFileCount: -1
                }
            }
        })

        const extractProfiles = await api.apiExtract.exportAll()
        const extractResult: ApiExtractProfileEntry[] = extractProfiles.objects.map(x => ({
            name: x.connectorName,
            endpointCount: x.endpoints.length
        }))

        return {
            apiQuery: profileResult,
            apiExtract: extractResult,
        }
    } catch(e) {
        logError("An error occurred while scanning API Profiles", e)
        return {}
    }
}

export async function analyseWebhooks(ctx: ScanContext, projects: ProjectId[]): Promise<ScanResult["webhookDetails"]> {
    const {log, options, api} = ctx
    if(!options.analyseWebhooks) return undefined
    log("Analysing error reporting configuration")
    const results = await runPromises<ProjectId, ProjectErrorReporting>(undefined, projects, async (projectId) => {
        const debug = await api.project(projectId).debug()
        return {
            projectId,
            enabled: debug.errorReportingConfiguration?.manualEnabled || debug.errorReportingConfiguration?.scheduledEnabled
        }
    })
    return {
        enabled: results.some(x=>x.enabled),
        projectDetails: results
    }
}

export async function analyseOAuth(ctx: ScanContext): Promise<OAuthEntry[] | undefined> {
    const {options, api, log} = ctx
    if(!options.analyseOauthConnections) return undefined
    log("Scanning OAuth types")
    const oauth = await api.oauth.exportAll()
    return oauth.objects.map(x => ({
        name: x.name,
        serviceId: x.serviceID,
        type: x.type
    }))
}

export async function analysePasswordTypes(ctx: ScanContext, groups: string[]): Promise<PasswordInfo[] | undefined> {
    const {api, log, options} = ctx
    if(!options.analysePasswords) return undefined
    log("Analysing password types")
    const results = await runPromises(undefined, groups, async (group)=> {
        const passwords = await api.groups.get(group).passwords.exportAll()
        return passwords.objects.map(x=> ({
            name: x.name,
            groupName: group,
            type: x.extraInfo?.class ?? x.encryptionType
        }))
    })
    return results.flatMap(x=>x)
}

export async function analyseCredentials(ctx: ScanContext): Promise<CloudCredentialEntry[] | undefined> {
    const {api, log, options} = ctx
    if(!options.analyseCredentials) return undefined
    log("Analysing cloud credential types")
    const cred = api.credentials()
    const credResults = [
        ...((await cred.aws.list()).map(x => ({name: x, type: 'aws'}))),
        ...((await cred.gcp.list()).map(x => ({name: x, type: 'gcp'}))),
        ...((await cred.azure.list()).map(x => ({name: x, type: 'azure'})))
    ]
    return credResults
}

export async function analyseQueue(ctx: ScanContext): Promise<QueueInfo | undefined> {
    const {options, log, api} = ctx
    if(!options.analyseQueueConfig) return undefined
    log("Analysing queue configuration")
    const queueExport = await api.queue().export()
    return {
        listen: isQueueEnabled(queueExport),
        cancel: queueExport.cancelEnabled,
        failure: queueExport.failureEnabled,
        success: queueExport.successEnabled,
    }
}

async function safely<T>(name: string, ctx: ScanContext, run: () => Promise<T>): Promise<T | undefined> {
    const {logError} = ctx
    try {
        return await run()
    } catch (e) {
        logError(`An error occured while scanning ${name}`, e)
    }
}

export async function optionalScans(ctx: ScanContext, versionsScanPlans: VersionsScanPlan): Promise<Partial<ScanResult>> {
    const distinctGroups: Set<string> = new Set<string>()
    const distinctProjects: Record<string, ProjectId> = {}
    versionsScanPlans.versions.forEach(v => {
        const projectKey = `${v.id.groupName} / ${v.id.projectName}`
        distinctProjects[projectKey] = v.id
        distinctGroups.add(v.id.groupName)
    })
    const groupNames = [...distinctGroups.values()]
    const projectIds = Object.values(distinctProjects)
    return {
        webhookDetails: await safely("Error Configuration", ctx, () => analyseWebhooks(ctx, projectIds)),
        oauth: await safely("OAuth Connections", ctx, () => analyseOAuth(ctx)),
        projectScm: await safely("SCM", ctx, ()=>scanSCM(ctx, projectIds)),
        passwords: await safely("Password Types", ctx, ()=>analysePasswordTypes(ctx, groupNames)),
        cloudCredentials: await safely("Cloud Credential Types", ctx, ()=>analyseCredentials(ctx)),
        queue: await safely("Queue Configuration", ctx, ()=>analyseQueue(ctx)),
        ...(await safely("API Profiles", ctx, ()=>analyseApiProfiles(ctx)))
    }
}